import React, { useEffect, useState } from 'react';
import { ShieldCheck, Pencil, FileText, Trash2, Globe, Mail, Phone, Hash, Copy, Check, ExternalLink, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthImg } from './AuthImg';
import { FileDropzone } from './FileDropzone';
import { GesellschaftCombobox } from './GesellschaftCombobox';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMPTY = { gesellschaft: '', versicherungsnummer: '', email: '', telefon: '', webseite: '', notizen: '' };

const MIME_BY_EXT = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };

/** Öffnet ein Dokument in der In-App-Vorschau (kein Download) – gehostet von GlobalDocPreview in App.js. */
export const openDocument = (docId, name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  window.dispatchEvent(new CustomEvent('hv:doc-preview', { detail: { id: docId, name: name || 'Dokument.pdf', file_type: MIME_BY_EXT[ext] ? ext : 'pdf' } }));
};

/** Lädt ein Dokument mit korrektem Dateinamen und Typ herunter. */
export const downloadDocument = async (docId, name) => {
  const r = await fetch(`${API}/api/documents/download/${docId}`, { headers: auth() });
  if (!r.ok) { toast.error('Dokument konnte nicht geladen werden'); return; }
  const ext = (name || '').split('.').pop().toLowerCase();
  const headerType = (r.headers.get('content-type') || '').split(';')[0].trim();
  const type = (headerType && headerType !== 'application/octet-stream') ? headerType : (MIME_BY_EXT[ext] || 'application/pdf');
  const url = URL.createObjectURL(new Blob([await r.blob()], { type }));
  const fileName = name && MIME_BY_EXT[ext] ? name : `${name || 'Dokument'}.${Object.keys(MIME_BY_EXT).find(k => MIME_BY_EXT[k] === type) || 'pdf'}`;
  const a = document.createElement('a'); a.href = url; a.download = fileName; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const VersicherungLogo = ({ v, className = 'w-10 h-10' }) => {
  const [err, setErr] = useState(false);
  if (!v?.logo_url || err) return <div className={`${className} rounded-lg bg-brand-navy/5 flex items-center justify-center`}><ShieldCheck className="w-5 h-5 text-brand-navy/50" /></div>;
  const img = v.logo_url.startsWith('/api')
    ? <AuthImg src={`${API}${v.logo_url}`} alt={v.gesellschaft} onError={() => setErr(true)} className="w-full h-full object-contain" />
    : <img src={v.logo_url} alt={v.gesellschaft} onError={() => setErr(true)} className="w-full h-full object-contain" />;
  return <div className={`${className} rounded-lg bg-white border border-gray-200 p-1 overflow-hidden`} data-testid="versicherung-logo">{img}</div>;
};

/** Admin-Karte „Gebäudeversicherung“ am Objekt: Stammdaten, Logo, Versicherungsschein (→ Objektdokumente, für Eigentümer sichtbar). */
export const VersicherungCard = ({ propertyId, onChanged }) => {
  const [v, setV] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const r = await fetch(`${API}/api/versicherung/properties/${propertyId}`, { headers: auth() });
    if (r.ok) setV(await r.json());
  };
  useEffect(() => { load(); }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [uebernommen, setUebernommen] = useState(null);
  const startEdit = () => { setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, v?.[k] || ''])) }); setUebernommen(null); setEdit(true); };
  const pickGesellschaft = (g) => {
    setForm(f => ({ ...f, gesellschaft: g.gesellschaft, email: g.email || f.email, telefon: g.telefon || f.telefon, webseite: g.webseite || f.webseite, logo_von_property_id: g.logo_von_property_id || undefined }));
    setUebernommen(g);
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/versicherung/properties/${propertyId}`, { method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!r.ok) throw new Error();
      setV(await r.json()); setEdit(false); toast.success('Versicherungsdaten gespeichert'); onChanged?.();
    } catch { toast.error('Speichern fehlgeschlagen'); } finally { setBusy(false); }
  };
  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API}/api/versicherung/properties/${propertyId}/${kind}`, { method: 'POST', headers: auth(), body: fd });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || ''); }
      setV(await r.json()); toast.success(kind === 'logo' ? 'Logo gespeichert' : 'Versicherungsschein in den Objektdokumenten abgelegt'); onChanged?.();
    } catch (e) { toast.error(e.message || 'Upload fehlgeschlagen'); } finally { setBusy(false); }
  };
  const remove = async (kind) => {
    if (!window.confirm(kind === 'logo' ? 'Logo entfernen?' : 'Versicherungsschein aus den Objektdokumenten entfernen?')) return;
    const r = await fetch(`${API}/api/versicherung/properties/${propertyId}/${kind}`, { method: 'DELETE', headers: auth() });
    if (r.ok) { setV(await r.json()); onChanged?.(); }
  };
  const copyNr = () => { navigator.clipboard?.writeText(v.versicherungsnummer); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const input = (k, label, ph, type = 'text') => (
    <div key={k}>
      <label className="block text-[11px] font-medium text-gray-600 mb-0.5">{label}</label>
      <input type={type} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} placeholder={ph} data-testid={`versicherung-${k}`}
        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-navy/20 outline-none" />
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="versicherung-card">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-gold" /><h3 className="font-bold text-brand-navy text-sm">Gebäudeversicherung</h3></div>
        {!edit && <button onClick={startEdit} className="p-1 text-gray-400 hover:text-brand-navy" title="Versicherung bearbeiten" data-testid="versicherung-edit-btn"><Pencil className="w-4 h-4" /></button>}
      </div>
      {edit ? (
        <div className="p-4 space-y-3" data-testid="versicherung-form">
          <GesellschaftCombobox value={form.gesellschaft} onChange={(val) => { setForm(f => ({ ...f, gesellschaft: val, logo_von_property_id: undefined })); setUebernommen(null); }} onPick={pickGesellschaft} placeholder="z. B. Allianz Versicherungs-AG" />
          {uebernommen && <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5 -mt-1" data-testid="versicherung-uebernommen-hinweis">Kontaktdaten{uebernommen.logo_von_property_id ? ' und Logo' : ''} von „{uebernommen.gesellschaft}“ übernommen ({uebernommen.objekt_namen.filter(Boolean).join(', ')}). Versicherungsscheinnummer bitte für dieses Objekt eintragen.</p>}
          {input('versicherungsnummer', 'Versicherungsscheinnummer', 'z. B. GV-123456789')}
          {input('email', 'E-Mail für Schadenmeldungen', 'schaden@versicherung.de', 'email')}
          <div className="grid grid-cols-2 gap-2">{input('telefon', 'Schadenhotline', '0800 …')}{input('webseite', 'Webseite (Logo-Quelle)', 'www.allianz.de')}</div>
          <div><label className="block text-[11px] font-medium text-gray-600 mb-0.5">Notizen (Selbstbehalt, Deckung, Makler …)</label>
            <textarea rows={2} value={form.notizen} onChange={e => setForm(f => ({ ...f, notizen: e.target.value }))} data-testid="versicherung-notizen"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-brand-navy/20 outline-none" /></div>
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setEdit(false)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Abbrechen</button>
            <button onClick={save} disabled={busy} data-testid="versicherung-save-btn" className="px-4 py-1.5 text-xs font-semibold bg-brand-gold text-brand-navy rounded-lg hover:bg-brand-gold-light disabled:opacity-50 flex items-center gap-1.5">
              {busy && <Loader2 className="w-3 h-3 animate-spin" />} Speichern</button>
          </div>
        </div>
      ) : !v ? <p className="p-4 text-xs text-gray-400">Lädt …</p> : !v.vorhanden ? (
        <div className="p-4 text-center">
          <p className="text-xs text-gray-400 mb-2">Noch keine Gebäudeversicherung hinterlegt.</p>
          <button onClick={startEdit} data-testid="versicherung-anlegen-btn" className="text-xs font-semibold text-brand-navy underline decoration-brand-gold/60 underline-offset-2">Jetzt hinterlegen</button>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <VersicherungLogo v={v} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-800 truncate" data-testid="versicherung-gesellschaft-text">{v.gesellschaft || 'Gesellschaft fehlt'}</p>
              {v.versicherungsnummer && (
                <button onClick={copyNr} className="flex items-center gap-1 text-xs text-gray-600 hover:text-brand-navy font-mono" title="Versicherungsnummer kopieren" data-testid="versicherung-nr-text">
                  <Hash className="w-3 h-3" />{v.versicherungsnummer}{copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 opacity-50" />}
                </button>
              )}
            </div>
          </div>
          <div className="space-y-1 text-[11px] text-gray-500">
            {v.email && <p className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" /><a href={`mailto:${v.email}`} className="hover:text-brand-navy truncate">{v.email}</a></p>}
            {v.telefon && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{v.telefon}</p>}
            {v.webseite && <p className="flex items-center gap-1.5 truncate"><Globe className="w-3 h-3" /><a href={v.webseite.startsWith('http') ? v.webseite : `https://${v.webseite}`} target="_blank" rel="noopener noreferrer" className="hover:text-brand-navy truncate">{v.webseite}</a></p>}
            {!v.email && <p className="text-amber-600">E-Mail für Schadenmeldungen fehlt – bitte ergänzen.</p>}
          </div>
          {v.notizen && <p className="text-[11px] text-gray-600 bg-gray-50 rounded-lg p-2 whitespace-pre-wrap">{v.notizen}</p>}
          <div className="pt-2 border-t border-gray-100 space-y-1.5">
            {v.schein_doc_id && (
              <div className="flex items-center gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-red-500 shrink-0" />
                <button onClick={() => openDocument(v.schein_doc_id, v.schein_name)} data-testid="versicherung-schein-link" className="text-brand-navy underline decoration-brand-gold/60 underline-offset-2 truncate text-left flex-1">{v.schein_name || 'Versicherungsschein'}</button>
                <button onClick={() => remove('schein')} className="p-1 text-gray-300 hover:text-red-500" title="Entfernen"><Trash2 className="w-3 h-3" /></button>
              </div>
            )}
            <FileDropzone testId="versicherung-schein-dropzone" inputTestId="versicherung-schein-input" accept=".pdf,image/*" busy={busy}
              size={v.schein_doc_id ? 'sm' : 'md'} onFiles={([f]) => upload('schein', f)} busyLabel="Wird hochgeladen …"
              label={v.schein_doc_id ? 'Neuen Schein hierher ziehen oder klicken' : 'Versicherungsschein hierher ziehen'}
              hint={v.schein_doc_id ? null : 'oder klicken zum Auswählen · PDF oder Bild, z. B. direkt aus Outlook'} />
            <div className="flex flex-wrap gap-2">
              <FileDropzone variant="inline" testId="versicherung-logo-upload-btn" inputTestId="versicherung-logo-input" accept="image/*" busy={busy}
                onFiles={([f]) => upload('logo', f)} className="!text-[11px] !py-1 !px-2 !text-gray-600 !border-gray-200" label={v.has_logo ? 'Logo ersetzen' : 'Logo hochladen · hierher ziehen'} />
              {v.has_logo && <button onClick={() => remove('logo')} className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-red-500 px-1"><X className="w-3 h-3" /> Logo</button>}
            </div>
            <p className="text-[10px] text-gray-400 flex items-center gap-1"><ExternalLink className="w-2.5 h-2.5" /> Schein wird passend benannt in den Objektdokumenten abgelegt und Eigentümern im Portal angezeigt.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersicherungCard;
